import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientHeader from '../../components/ui/ClientHeader';
import ClientProfileSidebar from '../../components/ui/ClientProfileSidebar';
import InventoryCard from './components/InventoryCard';
import ItemDetailModal from './components/ItemDetailModal';
import FilterControls from './components/FilterControls';
import StatsOverview from './components/StatsOverview';
import EmptyState from './components/EmptyState';

import Button from '../../components/ui/Button';

import { getClientInventory, getInventoryStats } from '../../services/laseaInventoryService';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryStats, setInventoryStats] = useState({
    totalItems: 0,
    itemsInRoute: 0,
    totalInventoryValue: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!userProfile?.id) return;

      try {
        setLoading(true);

        // Fetch inventory statistics from Supabase
        const stats = await getInventoryStats(userProfile?.id);
        setInventoryStats(stats);

        // Fetch recent inventory items
        const inventoryItems = await getClientInventory(userProfile?.id, { isActive: true });
        setInventoryItems(inventoryItems?.slice(0, 6)); // Show only 6 most recent items
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error?.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userProfile?.id]);

  const calculateStats = () => {
    const total = inventoryItems?.length;
    const perfect = inventoryItems?.filter((item) => item?.status?.toLowerCase() === 'perfect')?.length;
    const damaged = inventoryItems?.filter((item) => item?.status?.toLowerCase() === 'damaged')?.length;

    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
    const recentArrivals = inventoryItems?.filter((item) =>
      new Date(item.arrivalDate) >= sevenDaysAgo
    )?.length;

    return { total, perfect, damaged, recentArrivals };
  };

  const filterAndSortItems = () => {
    let filtered = [...(inventoryItems || [])];

    if (searchQuery) {
      filtered = filtered?.filter((item) =>
        item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.liin?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((item) =>
        item?.status?.toLowerCase() === statusFilter?.toLowerCase()
      );
    }

    switch (sortBy) {
      case 'newest':
        filtered?.sort((a, b) => new Date(b.arrivalDate) - new Date(a.arrivalDate));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a.arrivalDate) - new Date(b.arrivalDate));
        break;
      case 'name-asc':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'name-desc':
        filtered?.sort((a, b) => b?.name?.localeCompare(a?.name));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredItems = filterAndSortItems();
  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader />
      <div className="flex pt-16">
        <ClientProfileSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)} />


        <main className="flex-1 p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                  My Inventory
                </h1>
                <p className="text-sm text-muted-foreground">
                  Track and manage your warehoused furniture items
                </p>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden"
                iconName="User"
                iconSize={20}>

                <span className="sr-only">Open profile</span>
              </Button>
            </div>

            <StatsOverview stats={inventoryStats} />

            <FilterControls
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              sortBy={sortBy}
              onSortChange={setSortBy}
              totalItems={inventoryItems?.length}
              filteredCount={filteredItems?.length} />


            {filteredItems?.length === 0 ?
              <EmptyState searchQuery={searchQuery} /> :

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredItems?.map((item) =>
                  <InventoryCard
                    key={item?.id}
                    item={item}
                    onViewDetails={setSelectedItem}
                  />
                )}
              </div>
            }
          </div>
        </main>
      </div>

      <ItemDetailModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </div>);

};

export default ClientDashboard;